import React, { useEffect, useMemo, useState } from "react";
import { Modal, Select, Button } from "antd";
import { useTranslation } from "react-i18next";
import { apiRequest } from "util/services";
import {
  filterSelectOption,
  mergeMetricListLabels,
  sortSourcesByLastUpdate,
} from "./kpiViewUtils";

export default function AddMetricModal({
  open,
  onClose,
  sources = [],
  catalogMetrics = [],
  presetMetric = null,
  onSubmit,
  loading = false,
}) {
  const { t } = useTranslation();
  const [dashboardIds, setDashboardIds] = useState([]);
  const [metric, setMetric] = useState(presetMetric?.label || null);
  const [metricOptions, setMetricOptions] = useState([]);
  const [metricsLoading, setMetricsLoading] = useState(false);

  const sortedSources = useMemo(
    () => sortSourcesByLastUpdate(sources),
    [sources]
  );

  const dashboardOptions = useMemo(
    () =>
      sortedSources.map((s) => ({
        value: s.id,
        label: s.Name,
      })),
    [sortedSources]
  );

  const metricSelectOptions = useMemo(
    () =>
      metricOptions.map((label) => ({
        value: label,
        label,
      })),
    [metricOptions]
  );

  useEffect(() => {
    if (open) {
      setMetric(presetMetric?.label || null);
      if (presetMetric?.sourceId) {
        setDashboardIds([presetMetric.sourceId]);
      } else {
        setDashboardIds(sortedSources.length ? [sortedSources[0].id] : []);
      }
    }
  }, [open, presetMetric, sortedSources]);

  useEffect(() => {
    if (!open || dashboardIds.length === 0) {
      setMetricOptions([]);
      return;
    }

    let cancelled = false;
    const loadMetrics = async () => {
      setMetricsLoading(true);
      const result = await apiRequest(
        "executive_summary/v2/metricList",
        "POST",
        {
          dashboard_ids: dashboardIds.map((id) => parseInt(id, 10)),
          block_location: "Top",
        },
        true
      );
      if (cancelled) return;
      const labels = mergeMetricListLabels(result?.success);
      setMetricOptions(labels);
      setMetric((current) =>
        current && !labels.includes(current) ? null : current
      );
      setMetricsLoading(false);
    };

    loadMetrics();
    return () => {
      cancelled = true;
    };
  }, [open, dashboardIds]);

  const handleSubmit = async () => {
    if (!onSubmit) return;
    const ok = await onSubmit({
      metric,
      dashboardIds,
    });
    if (ok && onClose) onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={t("team_kpi_view.19")}
      destroyOnClose
      width={400}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <div style={{ fontSize: 12, marginBottom: 6, opacity: 0.7 }}>
            {t("team_kpi_view.5")}
          </div>
          <Select
            mode="multiple"
            style={{ width: "100%" }}
            placeholder={t("team_kpi_view.6")}
            value={dashboardIds}
            onChange={setDashboardIds}
            showSearch
            optionFilterProp="label"
            filterOption={filterSelectOption}
            options={dashboardOptions}
          />
        </div>
        <div>
          <div style={{ fontSize: 12, marginBottom: 6, opacity: 0.7 }}>
            {t("team_kpi_view.13")}
          </div>
          <Select
            style={{ width: "100%" }}
            placeholder={t("team_kpi_view.12")}
            value={metric}
            onChange={setMetric}
            showSearch
            loading={metricsLoading}
            optionFilterProp="label"
            filterOption={filterSelectOption}
            options={metricSelectOptions}
          />
        </div>
        <Button
          type="primary"
          block
          loading={loading}
          onClick={handleSubmit}
          disabled={!metric || dashboardIds.length === 0}
        >
          + {t("team_kpi_view.19")}
        </Button>
      </div>
    </Modal>
  );
}
